import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Button } from "@/components/ui/button";
import { Heart, Check } from "lucide-react";

/**
 * /i-sent-it?issue=<slug|number> landing page — a shareable "I sent it" link (e.g. from Discord).
 * On load, records the anonymous event and shows a warm thank-you screen with the current total.
 */
export default function ISentItPage() {
  const [params] = useSearchParams();
  const issueParam = params.get("issue");
  const token = params.get("t") || null;

  const [state, setState] = useState({ loading: true, ok: false, recorded: false, total: null, issue: null, message: "" });

  useEffect(() => {
    if (!issueParam) {
      setState({ loading: false, ok: false, recorded: false, total: null, issue: null, message: "Missing issue reference." });
      return;
    }
    const url = new URLSearchParams({ issue: issueParam });
    if (token) url.set("t", token);
    api.get(`/public/i-sent-it?${url.toString()}`)
      .then((r) => setState({ loading: false, ok: true, recorded: !!r.data?.recorded, total: r.data?.total ?? null, issue: r.data?.issue || null, message: "" }))
      .catch((e) => {
        const msg = e?.response?.status === 429 ? "Too many taps \u2014 give it a moment." : (e?.response?.data?.detail || "Could not record");
        setState({ loading: false, ok: false, recorded: false, total: null, issue: null, message: msg });
      });
  }, [issueParam, token]);

  return (
    <PublicLayout>
      <section className="hero-wash">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-24">
          <div className="kicker inline-flex items-center gap-2"><Heart className="h-3.5 w-3.5" /> Human Count</div>
          {state.loading ? (
            <div className="mt-6 nb-box p-8"><div className="h-24 bg-[color:var(--coh-paper)] border-2 border-[color:var(--coh-ink)] animate-pulse" /></div>
          ) : state.ok ? (
            <div data-testid="i-sent-it-page">
              <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-[-0.03em] text-[color:var(--coh-ink)]">
                {state.recorded ? "Counted. Thank you." : "Already counted \u2014 thank you."}
              </h1>
              <p className="mt-4 text-lg text-[color:var(--coh-ink-muted)] max-w-xl">
                Small actions add up. You just contributed one to the human count — an anonymous tally of every reconnection sparked by Cohnect.
              </p>
              <div className="mt-8 nb-box p-8">
                <div className="flex items-center gap-5">
                  <div className="h-14 w-14 shrink-0 border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] flex items-center justify-center text-[color:var(--coh-on-accent)]">
                    <Check className="h-7 w-7" />
                  </div>
                  <div>
                    <div className="kicker">Total humans reconnected</div>
                    <div data-testid="i-sent-it-total" className="mt-1 font-display font-black text-4xl sm:text-5xl text-[color:var(--coh-blue)] leading-none">
                      {new Intl.NumberFormat().format(state.total || 0)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {state.issue?.slug && (
                  <Button asChild className="btn-nb btn-nb-secondary h-11 px-5">
                    <Link to={`/issues/${state.issue.slug}`}>Back to this week&apos;s issue</Link>
                  </Button>
                )}
                <Button asChild className="btn-nb btn-nb-primary h-11 px-5">
                  <Link to="/">Home <span aria-hidden="true" className="font-mono ml-1">↗</span></Link>
                </Button>
              </div>
            </div>
          ) : (
            <div data-testid="i-sent-it-error">
              <h1 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-[color:var(--coh-ink)]">We couldn&apos;t record that.</h1>
              <p className="mt-3 text-[color:var(--coh-ink-muted)]">{state.message}</p>
              <div className="mt-6"><Button asChild className="btn-nb btn-nb-secondary h-11 px-5"><Link to="/">Back home</Link></Button></div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
